import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ScrollToTop } from "./ScrollToTop";
import { usePageMeta } from "../utils/usePageMeta";

// Totes les fotos són material de premsa de Porsche AG (newsroom.porsche.com),
// baixades el 16/10/2026 i convertides a webp. La tipografia de Porsche (Porsche
// Next) és d'ús exclusiu de la marca; aquí s'imita amb Archivo, que té eix
// d'amplada: l'expandida fa de logotip i la normal de text.
const IMG = (n: string) => `/histories/porsche/${n}.webp`;

const FONT_URL =
  "https://fonts.googleapis.com/css2?family=Archivo:wdth,wght@62..125,300..800&display=swap";

const css = `
.hp {
  --hp-bg: #ffffff;
  --hp-fg: #010205;
  --hp-muted: #5f6266;
  --hp-soft: #eeeff2;
  --hp-line: #d8d9dc;
  --hp-dark: #0e0e12;
  --hp-red: #d5001c;
  background: var(--hp-bg);
  color: var(--hp-fg);
  font-family: "Archivo", ui-sans-serif, system-ui, sans-serif;
  font-stretch: 100%;
  overflow-x: clip;
}
.dark .hp {
  --hp-bg: #0e0e12;
  --hp-fg: #fbfcff;
  --hp-muted: #a7a9ad;
  --hp-soft: #1a1b1f;
  --hp-line: #2c2d32;
  --hp-dark: #000000;
}
.hp-wide { font-stretch: 125%; }
.hp-mark { font-stretch: 125%; font-weight: 700; letter-spacing: 0.32em; text-transform: uppercase; }
.hp-kicker { font-size: 0.75rem; letter-spacing: 0.18em; text-transform: uppercase; color: var(--hp-muted); font-weight: 500; }
.hp-h2 { font-stretch: 112%; font-weight: 600; letter-spacing: -0.01em; line-height: 1.02; font-size: clamp(2.2rem, 6vw, 4.6rem); }
.hp-h3 { font-stretch: 108%; font-weight: 600; line-height: 1.1; font-size: clamp(1.35rem, 2.6vw, 2rem); }
.hp-lead { font-size: clamp(1.05rem, 1.6vw, 1.3rem); line-height: 1.55; color: var(--hp-muted); font-weight: 400; }
.hp-p { font-size: 1rem; line-height: 1.7; color: var(--hp-muted); }
.hp-p strong { color: var(--hp-fg); font-weight: 600; }
.hp-muted { color: var(--hp-muted); }
.hp-soft { background: var(--hp-soft); }
.hp-line { border-color: var(--hp-line); }
.hp-darkband { --hp-bg: var(--hp-dark); background: var(--hp-bg); color: #fbfcff; --hp-muted: #a7a9ad; --hp-line: #2c2d32; --hp-soft: #1a1b1f; --hp-fg: #fbfcff; }
.hp-red { color: var(--hp-red); }
.hp-cap { font-size: 0.72rem; color: var(--hp-muted); letter-spacing: 0.02em; margin-top: 0.6rem; }
.hp-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.hp-num { font-stretch: 125%; font-weight: 300; font-variant-numeric: tabular-nums; line-height: 1; }

.hp-reveal { opacity: 0; transform: translateY(12px); transition: opacity 0.45s ease-out, transform 0.45s ease-out; }
.hp-reveal.is-in { opacity: 1; transform: none; }
.hp-zoom { overflow: hidden; }
.hp-zoom img { transition: transform 1.2s cubic-bezier(.2,.7,.2,1); }
.hp-zoom:hover img { transform: scale(1.035); }

.hp-hero-img { animation: hp-kenburns 18s ease-out both; }
@keyframes hp-kenburns { from { transform: scale(1.12); } to { transform: scale(1); } }
.hp-fadeup { animation: hp-fadeup 1.2s cubic-bezier(.2,.7,.2,1) both; }
@keyframes hp-fadeup { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }

.hp-btn { display: inline-flex; align-items: center; gap: .5rem; padding: .7rem 1.2rem; border-radius: 4px; font-size: .9rem; font-weight: 500; border: 1px solid var(--hp-fg); color: var(--hp-fg); transition: background .2s, color .2s; }
.hp-btn:hover { background: var(--hp-fg); color: var(--hp-bg); }
.hp-chip { padding: .45rem .8rem; border-radius: 4px; font-size: .85rem; border: 1px solid var(--hp-line); transition: all .2s; cursor: pointer; }
.hp-chip:hover { border-color: var(--hp-fg); }
.hp-chip.on { background: var(--hp-fg); color: var(--hp-bg); border-color: var(--hp-fg); }

.hp-strip { scrollbar-width: none; }
.hp-strip::-webkit-scrollbar { display: none; }

.hp-strike { position: relative; display: inline-block; }
.hp-strike::after { content: ""; position: absolute; left: -6%; right: -6%; top: 52%; height: 0.06em; background: var(--hp-red); transform: scaleX(0); transform-origin: left; transition: transform .6s cubic-bezier(.2,.7,.2,1); }
.hp-strike.on::after { transform: scaleX(1); }

@media (prefers-reduced-motion: reduce) {
  .hp-reveal { opacity: 1; transform: none; transition: none; }
  .hp-zoom img, .hp-hero-img, .hp-fadeup { transition: none; animation: none; }
}
`;

// Fa aparèixer els elements amb .hp-reveal quan entren a la pantalla.
const useReveal = () => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const els = root.querySelectorAll(".hp-reveal");
    if (!("IntersectionObserver" in window)) {
      els.forEach((e) => e.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add("is-in");
            io.unobserve(en.target);
          }
        }),
      { rootMargin: "0px 0px 20% 0px", threshold: 0 }
    );
    els.forEach((e) => io.observe(e));
    return () => io.disconnect();
  }, []);
  return ref;
};

const useFont = () => {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_URL}"]`)) return;
    const pre = document.createElement("link");
    pre.rel = "preconnect";
    pre.href = "https://fonts.gstatic.com";
    pre.crossOrigin = "";
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = FONT_URL;
    document.head.append(pre, l);
  }, []);
};

const Foto = ({
  src,
  alt,
  cap,
  ratio = "16/9",
  className = "",
  eager = false,
}: {
  src: string;
  alt: string;
  cap?: string;
  ratio?: string;
  className?: string;
  eager?: boolean;
}) => (
  <figure className={`hp-reveal ${className}`}>
    <div className="hp-zoom hp-soft" style={{ aspectRatio: ratio }}>
      <img
        src={IMG(src)}
        alt={alt}
        className="hp-img"
        loading={eager ? "eager" : "lazy"}
        decoding="async"
      />
    </div>
    {cap && <figcaption className="hp-cap">{cap} · © Porsche AG</figcaption>}
  </figure>
);

const Capitol = ({ n, titol, id }: { n: string; titol: string; id: string }) => (
  <div id={id} className="scroll-mt-20 hp-reveal flex items-baseline gap-4 mb-8 sm:mb-12">
    <span className="hp-kicker">{n}</span>
    <span className="h-px flex-1 border-t hp-line" />
    <span className="hp-kicker">{titol}</span>
  </div>
);

const Wrap = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-8 ${className}`}>{children}</div>
);

/* ---------------------------------- Dades --------------------------------- */

const capitols = [
  { id: "origens", n: "01", t: "Orígens" },
  { id: "escut", n: "02", t: "L'escut" },
  { id: "propietat", n: "03", t: "De qui és" },
  { id: "911", n: "04", t: "El 911" },
  { id: "noms", n: "05", t: "Els noms" },
  { id: "peugeot", n: "06", t: "El cas Peugeot" },
  { id: "models", n: "07", t: "Models" },
  { id: "avui", n: "08", t: "Avui" },
];

const generacions = [
  {
    codi: "F",
    nom: "Model original",
    anys: "1963–1973",
    img: "911-f",
    foto: "911 S Targa (1970)",
    text: "Neix com a 901 al Saló de Frankfurt i es converteix en 911 abans d'arribar als concessionaris. Motor bòxer de sis cilindres refrigerat per aire, darrere de l'eix posterior: la fórmula que encara dura. El dissenya Ferdinand Alexander «Butzi» Porsche, el nét del fundador.",
  },
  {
    codi: "G",
    nom: "Sèrie G",
    anys: "1973–1989",
    img: "911-g",
    foto: "911 Carrera (1975)",
    text: "Para-xocs amb fuelles per complir la normativa americana de xocs a 8 km/h. És l'època del primer 911 Turbo (1974) i la generació més longeva: 16 anys i gairebé 200.000 unitats.",
  },
  {
    codi: "964",
    nom: "Tipus 964",
    anys: "1989–1994",
    img: "911-964",
    foto: "911 Carrera RS (1991)",
    text: "Per fora sembla el mateix; per dins, el 85% de peces són noves. Estrena la tracció total al 911 (Carrera 4), l'ABS, la direcció assistida i l'aleró que puja sol a 80 km/h.",
  },
  {
    codi: "993",
    nom: "Tipus 993",
    anys: "1993–1998",
    img: "911-993",
    foto: "911 Carrera (1997)",
    text: "L'últim 911 refrigerat per aire, i per a molts el més bonic. Fars més inclinats, carrosseria més ampla i suspensió posterior multibraç. Avui és dels 911 més cotitzats.",
  },
  {
    codi: "996",
    nom: "Tipus 996",
    anys: "1997–2005",
    img: "911-996",
    foto: "911 Carrera «40 anys del 911» (2003)",
    text: "El gran trencament: motor refrigerat per aigua i els fars «d'ou ferrat» compartits amb el Boxster. Va ser polèmic, però va salvar l'empresa, que just sortia d'una crisi seriosa.",
  },
  {
    codi: "997",
    nom: "Tipus 997",
    anys: "2004–2012",
    img: "911-997",
    foto: "911 Carrera 4S (2005)",
    text: "Torna als fars rodons. Més refinat i més ràpid, amb el canvi de doble embragatge PDK a partir del 2008 i el primer GT3 RS 4.0.",
  },
  {
    codi: "991",
    nom: "Tipus 991",
    anys: "2011–2019",
    img: "911-991",
    foto: "911 Carrera S «50 anys del 911» (2013)",
    text: "Nova plataforma, batalla més llarga i molt d'alumini. Des del 2015 els Carrera porten turbo. És el 911 més venut de la història: més de 200.000 unitats.",
  },
  {
    codi: "992",
    nom: "Tipus 992",
    anys: "2019–avui",
    img: "911-992",
    foto: "911 Carrera 4S",
    text: "Tots els 911 amb la carrosseria ampla, pilot posterior de banda a banda i quadre digital. La renovació del 2024 (992.2) estrena el primer 911 híbrid: el Carrera GTS T-Hybrid.",
  },
];

// Un sol nom desglossat: totes les peces visibles alhora.
const anatomia = [
  { t: "911", l: "Model", d: "El número de projecte (era 901)." },
  { t: "Carrera", l: "Versió", d: "Per la Carrera Panamericana. És el 911 de sèrie." },
  { t: "4", l: "Tracció", d: "Quatre rodes motrius." },
  { t: "GTS", l: "Nivell", d: "Gran Turismo Sport: més potència i equip esportiu." },
];

const nomsFamilies = [
  { n: "Boxster", o: "Boxer + roadster", d: "Motor bòxer i descapotable.", img: "boxster-986" },
  { n: "Cayman", o: "Caiman", d: "El Boxster tancat, «amb més mossegada».", img: "cayman" },
  { n: "Cayenne", o: "Pebre de Caiena", d: "Picant. El primer SUV de Porsche.", img: "cayenne-electric" },
  { n: "Panamera", o: "Carrera Panamericana", d: "La cursa mexicana que també dona nom al Carrera.", img: "panamera" },
  { n: "Macan", o: "Tigre, en indonesi", d: "El SUV compacte.", img: "macan" },
  { n: "Taycan", o: "Turquès", d: "«Ànima d'un cavall jove i ple d'energia».", img: "taycan" },
];

const sufixos = [
  { s: "S", d: "Més potència" },
  { s: "4", d: "Tracció total" },
  { s: "T", d: "Més lleuger i pur" },
  { s: "GTS", d: "Entre l'S i el Turbo" },
  { s: "Turbo", d: "El cim de la gamma (també als elèctrics)" },
  { s: "GT3 · RS", d: "Versions de circuit" },
  { s: "Targa", d: "Sostre que es treu, per la Targa Florio" },
  { s: "Spyder", d: "Obert i de motor central" },
];

const peugeotSeries = [
  { x: "2", models: "201 (1929) → 202 → 203 → 204 → 205 → 206 → 207 → 208", d: "Petit" },
  { x: "3", models: "301 → 304 → 305 → 306 → 307 → 308", d: "Compacte" },
  { x: "4", models: "401 → 402 → 403 → 404 → 405 → 406 → 407 → 408", d: "Mitjà" },
  { x: "5", models: "504 → 505 → 508", d: "Gran" },
  { x: "6", models: "601 → 604 → 605 → 607", d: "Luxe" },
];

type Fita = {
  any: string;
  nom: string;
  img: string;
  ratio?: string;
  text: string;
  dada?: { v: string; l: string };
  foto?: string;
};

const fites: Fita[] = [
  {
    any: "1953",
    nom: "550 Spyder",
    img: "550-spyder",
    text: "El primer Porsche fet expressament per córrer: motor central, carrosseria d'alumini i menys de 600 kg. Va guanyar la seva classe a la Carrera Panamericana i va passar per sota d'una barrera de tren tancada a la Mille Miglia. També és el cotxe amb què es va matar James Dean.",
    dada: { v: "~550", l: "kg en buit" },
  },
  {
    any: "1963",
    nom: "904 Carrera GTS",
    img: "904",
    text: "Carrosseria de fibra de vidre, també obra de Butzi Porsche. És el primer GTS, l'origen de la sigla que encara porten els Porsche d'avui.",
  },
  {
    any: "1969",
    nom: "914",
    img: "914",
    foto: "914/6, 1970",
    text: "Un projecte compartit amb Volkswagen: motor central, sostre Targa i preu accessible. A Europa es venia com a VW-Porsche. És l'avantpassat directe del Boxster.",
  },
  {
    any: "1970",
    nom: "917",
    img: "917-lemans",
    foto: "917 KH, 24 Hores de Le Mans, 1970",
    text: "El 14 de juny de 1970, Hans Herrmann i Richard Attwood guanyen les 24 Hores de Le Mans amb el 917 KH número 23. És la primera victòria absoluta de Porsche, que avui és la marca que més vegades ha guanyat la cursa.",
    dada: { v: "343", l: "voltes al circuit de la Sarthe" },
  },
  {
    any: "1972",
    nom: "911 Carrera RS 2.7",
    img: "carrera-rs",
    text: "L'«ala d'ànec» posterior i les lletres Carrera al lateral. Es van fer per homologar-lo per a curses i se'n van vendre més de 1.500. Va estrenar la sigla RS als carrers.",
  },
  {
    any: "1974",
    nom: "911 Turbo (930)",
    img: "930-turbo",
    text: "Turbocompressor, aleta posterior de «cua de balena» i passos de roda inflats. Un dels primers esportius de sèrie amb turbo i l'origen del mite de la paraula «Turbo».",
  },
  {
    any: "1986",
    nom: "959",
    img: "959",
    text: "El cotxe de sèrie més avançat del seu temps: tracció total controlada electrònicament, doble turbo i 450 CV. Pensat per al Grup B de ral·lis, va fer primer i segon al París-Dakar del 1986.",
    dada: { v: "317", l: "km/h de velocitat màxima" },
  },
];

const fites2: Fita[] = [
  {
    any: "1996",
    nom: "Boxster (986)",
    img: "boxster-986",
    text: "A principi dels noranta Porsche estava a prop de la fallida: el 1993 només va vendre uns 14.000 cotxes. El Boxster, més barat i amb moltes peces compartides amb el futur 911 (996), la va salvar.",
  },
  {
    any: "2002",
    nom: "Cayenne",
    img: "cayenne-2002",
    text: "Un SUV de Porsche. Els puristes es van esverar, però va vendre més que qualsevol altre model i va pagar els esportius. Es fabricava a Leipzig, en una fàbrica nova, i compartia base amb el VW Touareg.",
  },
  {
    any: "2003",
    nom: "Carrera GT",
    img: "carrera-gt",
    text: "Un V10 de 612 CV que venia d'un prototip de Le Mans que no va arribar a córrer, xassís de fibra de carboni i canvi manual. Se'n van fer 1.270.",
    dada: { v: "1.270", l: "unitats" },
  },
  {
    any: "2009",
    nom: "Panamera",
    img: "panamera-2009",
    text: "Quatre portes i quatre places de veritat, però amb l'ADN del 911. Porsche ho havia intentat des dels anys cinquanta (projecte 530, 989...).",
  },
  {
    any: "2013",
    nom: "918 Spyder",
    img: "918",
    text: "Híbrid endollable de 887 CV i el primer cotxe de sèrie que va baixar dels 7 minuts al Nürburgring (6:57). Se'n van fer 918, és clar.",
    dada: { v: "6:57", l: "al Nürburgring" },
  },
  {
    any: "2014",
    nom: "Macan",
    img: "macan",
    text: "El SUV compacte, fabricat a Leipzig. Des del 2024 la segona generació és 100% elèctrica i comparteix plataforma (PPE) amb l'Audi Q6 e-tron.",
  },
  {
    any: "2019",
    nom: "Taycan",
    img: "taycan",
    text: "Va començar com el concept Mission E (2015). Primer Porsche elèctric, amb arquitectura de 800 volts que li permet carregar molt de pressa. Es fabrica a Zuffenhausen, al costat del 911.",
  },
];

const gamma = [
  { n: "911", t: "Esportiu", m: "Combustió i híbrid", img: "911-actual" },
  { n: "718", t: "Boxster i Cayman", m: "Combustió, en retirada", img: "cayman" },
  { n: "Taycan", t: "Berlina i familiar", m: "Elèctric", img: "taycan-2" },
  { n: "Panamera", t: "Gran turisme", m: "Combustió i híbrid endollable", img: "panamera" },
  { n: "Macan", t: "SUV compacte", m: "Elèctric", img: "macan-2" },
  { n: "Cayenne", t: "SUV", m: "Combustió, híbrid i elèctric", img: "cayenne-electric" },
];

const fonts = [
  { nom: "The Porsche Code (Porsche Newsroom)", url: "https://newsroom.porsche.com/en_US/2022/products/porsche-code-terminology-model-name-in-house-type-number-27505.html" },
  { nom: "75 anys de Porsche", url: "https://newsroom.porsche.com/en/2023/history/porsche-75-years-people-models-milestones-31874.html" },
  { nom: "Les 8 generacions del 911", url: "https://newsroom.porsche.com/en/2019/history/porsche-klassik-911-eight-generations-dna-18914.html" },
  { nom: "L'escut de Porsche", url: "https://newsroom.porsche.com/en/2023/history/porsche-crest-quality-seal-christophorus-405-30636.html" },
  { nom: "El despatx d'enginyeria (1931–1951)", url: "https://newsroom.porsche.com/en/company/porsche-1931-1951-history-study-book-design-office-ferdinand-porsche-14144.html" },
  { nom: "Taycan: d'on ve el nom", url: "https://newsroom.porsche.com/en/products/porsche-taycan-name-meaning-soul-spirited-young-horse-electric-sports-car-electromobility-emobility-16292.html" },
  { nom: "50 anys de Transaxle", url: "https://newsroom.porsche.com/en/2026/history/porsche-transaxle-models-history-42008.html" },
  { nom: "Primera victòria a Le Mans", url: "https://newsroom.porsche.com/en/2020/history/porsche-le-mans-first-overall-victory-50-years-ago-917-kh-anniversary-weekend-21080.html" },
];

/* ------------------------------- Components ------------------------------- */

const SelectorGeneracions = () => {
  const [i, setI] = useState(7);
  const g = generacions[i];
  return (
    <div className="hp-reveal">
      <div className="relative hp-soft overflow-hidden" style={{ aspectRatio: "1440/812" }}>
        {generacions.map((x, k) => (
          <img
            key={x.codi}
            src={IMG(x.img)}
            alt={`Porsche 911 ${x.nom}`}
            loading="lazy"
            decoding="async"
            className="hp-img absolute inset-0 transition-opacity duration-700"
            style={{ opacity: k === i ? 1 : 0 }}
          />
        ))}
        <div className="absolute left-4 sm:left-8 top-4 sm:top-6 text-white mix-blend-difference">
          <div className="hp-num text-[18vw] sm:text-[9rem] leading-none select-none">{g.codi}</div>
        </div>
      </div>
      {/* Línia de temps */}
      <div className="relative mt-6">
        <div className="absolute left-0 right-0 top-[7px] border-t hp-line" />
        <div className="relative grid grid-cols-8">
          {generacions.map((x, k) => (
            <button
              key={x.codi}
              onClick={() => setI(k)}
              className="group flex flex-col items-center gap-2 pt-0 cursor-pointer"
              aria-label={`${x.nom}, ${x.anys}`}
              aria-pressed={k === i}
            >
              <span
                className="block w-[15px] h-[15px] rounded-full border-2 transition-all"
                style={{
                  borderColor: k === i ? "var(--hp-red)" : "var(--hp-line)",
                  background: k === i ? "var(--hp-red)" : "var(--hp-bg)",
                }}
              />
              <span
                className="text-[11px] sm:text-sm font-semibold transition-colors"
                style={{ color: k === i ? "var(--hp-fg)" : "var(--hp-muted)" }}
              >
                {x.codi}
              </span>
              <span className="hidden sm:block text-[11px] hp-muted">{x.anys.split("–")[0]}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="mt-8 grid md:grid-cols-12 gap-6">
        <div className="md:col-span-4">
          <div className="hp-kicker">{g.anys}</div>
          <h3 className="hp-h3 mt-2">{g.nom}</h3>
          <div className="hp-cap">Foto: {g.foto} · © Porsche AG</div>
        </div>
        <p className="md:col-span-8 hp-lead">{g.text}</p>
      </div>
    </div>
  );
};

const NouCentOnze = () => {
  const [on, setOn] = useState(false);
  return (
    <div className="hp-reveal">
      <button
        onClick={() => setOn((v) => !v)}
        className="hp-num select-none cursor-pointer flex items-baseline"
        style={{ fontSize: "clamp(6rem, 24vw, 17rem)", fontWeight: 600 }}
        aria-label="Canvia 901 per 911"
      >
        <span>9</span>
        <span className="relative inline-block" style={{ width: "0.62em" }}>
          <span
            className={`hp-strike absolute inset-0 transition-all duration-500 ${on ? "on" : ""}`}
            style={{ opacity: on ? 0.25 : 1, transform: on ? "translateY(-0.35em) scale(0.55)" : "none" }}
          >
            0
          </span>
          <span
            className="absolute inset-0 hp-red transition-all duration-500"
            style={{ opacity: on ? 1 : 0, transform: on ? "none" : "translateY(0.3em)" }}
          >
            1
          </span>
          <span className="invisible">0</span>
        </span>
        <span>1</span>
      </button>
      <p className="hp-cap">{on ? "Tardor de 1964: ja és un 911. Torna-hi a tocar." : "Toca el número."}</p>
    </div>
  );
};

// Botó per escoltar com es pronuncia «Porsche» (gravació d'un parlant alemany,
// Wikimedia Commons). L'OGG pesa poc; el WAV és per a Safari, que no llegeix Vorbis.
const Pronuncia = () => {
  const ref = useRef<HTMLAudioElement>(null);
  const [sona, setSona] = useState(false);
  return (
    <button
      onClick={() => {
        const a = ref.current;
        if (!a) return;
        a.currentTime = 0;
        a.play().catch(() => {});
      }}
      className="mt-7 inline-flex items-center gap-3 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 pl-2 pr-5 py-2 text-white transition-colors"
      aria-label="Escolta com es pronuncia Porsche"
    >
      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-white text-black">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
          <path d="M3 9v6h4l5 4V5L7 9H3z" />
          <path
            d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            style={{ opacity: sona ? 1 : 0.35, transition: "opacity .2s" }}
          />
        </svg>
      </span>
      <span className="text-left leading-tight">
        <span className="block text-sm sm:text-base font-medium">«Pór-xe», amb la e final</span>
        <span className="block text-xs text-white/60 font-mono">[ˈpɔʁʃə] · escolta-ho</span>
      </span>
      <audio ref={ref} preload="none" onPlay={() => setSona(true)} onEnded={() => setSona(false)} onPause={() => setSona(false)}>
        <source src={IMG("pronuncia").replace(".webp", ".ogg")} type="audio/ogg" />
        <source src={IMG("pronuncia").replace(".webp", ".wav")} type="audio/wav" />
      </audio>
    </button>
  );
};

const FitaBloc = ({ f, i }: { f: Fita; i: number }) => (
  <article className="grid md:grid-cols-12 gap-6 md:gap-10 items-center">
    <div className={`md:col-span-7 ${i % 2 ? "md:order-2" : ""}`}>
      <Foto src={f.img} alt={`Porsche ${f.nom}`} ratio={f.ratio ?? "16/10"} cap={f.foto ?? f.nom} />
    </div>
    <div className={`md:col-span-5 hp-reveal ${i % 2 ? "md:order-1" : ""}`}>
      <div className="hp-num text-5xl sm:text-6xl hp-muted">{f.any}</div>
      <h3 className="hp-h3 mt-3">{f.nom}</h3>
      <p className="hp-p mt-4">{f.text}</p>
      {f.dada && (
        <div className="mt-6 pt-5 border-t hp-line flex items-baseline gap-3">
          <span className="hp-num text-4xl font-semibold">{f.dada.v}</span>
          <span className="text-sm hp-muted">{f.dada.l}</span>
        </div>
      )}
    </div>
  </article>
);

/* --------------------------------- Pàgina --------------------------------- */

export const HistoriaPorsche = () => {
  usePageMeta({
    title: "Porsche: la història | Cotx.es",
    description:
      "La història de Porsche: Ferdinand i Ferry Porsche, el 356, l'escut, les 8 generacions del 911, d'on surten els noms dels models i per què Peugeot va obligar a canviar el 901.",
  });
  useFont();
  const ref = useReveal();

  return (
    <>
      <style>{css}</style>
      <div className="hp" ref={ref}>
        {/* HERO */}
        <section className="relative h-[88vh] min-h-[560px] max-h-[960px] overflow-hidden bg-black text-white">
          <img
            src={IMG("hero")}
            alt="Les vuit generacions del Porsche 911 en un hangar"
            className="hp-img hp-hero-img absolute inset-0"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-black/85" />
          <div className="relative h-full flex flex-col">
            <Wrap className="pt-8 sm:pt-12 w-full">
              <Link to="/histories" className="hp-kicker !text-white/70 hover:!text-white">
                ← Històries
              </Link>
            </Wrap>
            <div className="flex-1 flex items-center justify-center text-center px-4">
              <div className="hp-fadeup">
                <div className="hp-mark text-[9.5vw] sm:text-[7.5rem] leading-none pl-[0.32em]">Porsche</div>
                <p className="mt-6 text-base sm:text-xl text-white/90 font-light max-w-xl mx-auto [text-shadow:0_1px_12px_rgba(0,0,0,.6)]">
                  De la serradora de Gmünd al cotxe elèctric. 78 anys, vuit generacions de 911 i un cavall rampant.
                </p>
                <Pronuncia />
              </div>
            </div>
            <Wrap className="pb-6 sm:pb-10 w-full">
              <nav className="flex gap-x-5 gap-y-2 flex-wrap justify-center text-xs sm:text-sm text-white/70">
                {capitols.map((c) => (
                  <a key={c.id} href={`#${c.id}`} className="hover:text-white transition-colors">
                    <span className="text-white/40 mr-1">{c.n}</span>
                    {c.t}
                  </a>
                ))}
              </nav>
              <p className="mt-4 text-center text-[10px] text-white/45">Les 8 generacions del 911 · © Porsche AG</p>
            </Wrap>
          </div>
        </section>

        {/* 01 ORÍGENS */}
        <section className="py-20 sm:py-32">
          <Wrap>
            <Capitol n="01" titol="Orígens" id="origens" />
            <div className="grid md:grid-cols-12 gap-8 md:gap-12">
              <h2 className="hp-h2 md:col-span-7 hp-reveal">
                Abans de fer cotxes amb el seu nom, Porsche els feia per a tothom.
              </h2>
              <div className="md:col-span-5 hp-reveal">
                <p className="hp-lead">
                  <strong className="font-semibold" style={{ color: "var(--hp-fg)" }}>Ferdinand Porsche</strong> neix el
                  1875 a Maffersdorf, a Bohèmia (avui Txèquia). El 1898 ja dissenya el seu primer cotxe, elèctric, i el
                  1900 presenta a París un cotxe amb motors elèctrics a les rodes i, poc després, el primer híbrid.
                </p>
              </div>
            </div>

            <div className="mt-14 grid md:grid-cols-12 gap-6">
              <Foto
                src="lohner"
                alt="Ferdinand Porsche en un Lohner-Porsche elèctric el 1900"
                cap="Ferdinand Porsche al volant d'un Lohner-Porsche elèctric, 1900"
                className="md:col-span-7"
                ratio="4/3"
              />
              <div className="md:col-span-5 flex flex-col gap-6">
                <Foto
                  src="vw"
                  alt="Ferdinand Porsche i Karl Rabe amb un esbós del Volkswagen"
                  cap="Ferdinand Porsche i Karl Rabe amb un esbós del VW, 1938"
                  ratio="16/10"
                />
                <div className="hp-reveal hp-soft p-6 sm:p-8 flex-1">
                  <div className="hp-num text-6xl sm:text-7xl">1931</div>
                  <p className="hp-p mt-4">
                    Després de dirigir l'enginyeria d'Austro-Daimler i Daimler, obre a Stuttgart un{" "}
                    <strong>despatx d'enginyeria</strong> amb 19 empleats. Hi dissenyen per encàrrec: el Wanderer, el
                    cotxe de curses Auto Union i, sobretot, el <strong>Volkswagen Escarabat</strong> (projecte 60).
                  </p>
                </div>
              </div>
            </div>

            {/* Cronologia curta */}
            <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 border-t hp-line">
              {[
                { a: "1875", t: "Neix Ferdinand Porsche a Bohèmia." },
                { a: "1931", t: "Despatx d'enginyeria a Stuttgart." },
                { a: "1944", t: "Per la guerra, l'empresa es trasllada a Gmünd, als Alps austríacs." },
                { a: "1948", t: "El 356 «Nº 1», el primer cotxe que porta el nom Porsche." },
              ].map((x, k) => (
                <div key={x.a} className={`hp-reveal pt-6 pb-8 pr-6 ${k > 0 ? "lg:pl-6 lg:border-l hp-line" : ""}`}>
                  <div className="hp-num text-3xl sm:text-4xl">{x.a}</div>
                  <p className="text-sm hp-muted mt-3 leading-relaxed">{x.t}</p>
                </div>
              ))}
            </div>
          </Wrap>
        </section>

        {/* 1948: banda fosca */}
        <section className="hp-darkband py-20 sm:py-32">
          <Wrap>
            <div className="grid md:grid-cols-12 gap-8 items-end">
              <div className="md:col-span-5 hp-reveal">
                <div className="hp-kicker">8 de juny de 1948 · Gmünd, Àustria</div>
                <h2 className="hp-h2 mt-4">356 «Nº 1»</h2>
                <p className="hp-p mt-6">
                  Ferdinand havia passat gairebé dos anys empresonat a França després de la guerra. Mentrestant, el seu
                  fill <strong>Ferry</strong> porta l'empresa i, en una antiga serradora de Gmünd, fa realitat el cotxe
                  que vol: un esportiu petit i lleuger. El número 356 era, simplement, el projecte 356 del despatx.
                </p>
                <p className="hp-p mt-4">
                  Motor de Volkswagen retocat, xassís tubular i carrosseria d'alumini feta a mà. El 1950 tornen a
                  Stuttgart-Zuffenhausen, on encara són avui. Ferdinand mor el gener de 1951.
                </p>
              </div>
              <div className="md:col-span-7">
                <Foto src="356-no1" alt="Porsche 356 Nº 1 Roadster" cap="356 «Nº 1» Roadster, 1948" ratio="3/2" />
              </div>
            </div>

            <blockquote className="hp-reveal mt-20 sm:mt-28 max-w-4xl">
              <p className="hp-wide font-light leading-[1.2]" style={{ fontSize: "clamp(1.5rem, 3.4vw, 2.7rem)" }}>
                «Al principi vaig mirar al meu voltant i no vaig trobar el cotxe que somiava: un esportiu petit i
                lleuger que aprofités bé l'energia. Així que vaig decidir construir-lo jo mateix.»
              </p>
              <footer className="mt-6 hp-kicker">— Ferry Porsche</footer>
            </blockquote>

            <div className="mt-16 grid sm:grid-cols-2 gap-6">
              <Foto
                src="gmund"
                alt="Ferdinand Porsche, Ferry Porsche i Erwin Komenda amb el 356 Nº 1"
                cap="Ferdinand Porsche, Ferry Porsche i Erwin Komenda amb el 356 «Nº 1», 1948"
                ratio="16/10"
              />
              <Foto src="356-no1-bn" alt="El 356 Nº 1 a Gmünd" cap="El 356 «Nº 1» a Gmünd, 1948" ratio="16/10" />
            </div>
          </Wrap>
        </section>

        {/* 02 ESCUT */}
        <section className="py-20 sm:py-32">
          <Wrap>
            <Capitol n="02" titol="L'escut" id="escut" />
            <div className="grid md:grid-cols-12 gap-10 items-start">
              <div className="md:col-span-5">
                <Foto src="escut-1952" alt="Escut original de Porsche del 1952" cap="L'escut original, 1952" ratio="9/14" />
              </div>
              <div className="md:col-span-7">
                <h2 className="hp-h2 hp-reveal">Un cavall que ve d'un jardí d'egües.</h2>
                <p className="hp-lead mt-6 hp-reveal">
                  Segons la història més repetida, el 1951 Max Hoffman, l'importador de Porsche als Estats Units, va demanar un emblema que fes més
                  reconeixibles els cotxes. Ferry Porsche el va encarregar al dissenyador Franz Xaver Reimspieß, i des
                  del 1952 és al volant de cada Porsche.
                </p>
                <div className="mt-10 grid sm:grid-cols-2 gap-px hp-line border" style={{ background: "var(--hp-line)" }}>
                  {[
                    { t: "El cavall", d: "Ve de l'escut de Stuttgart. El nom de la ciutat ve de Stutengarten, «jardí d'egües»: s'hi criaven cavalls." },
                    { t: "«Stuttgart»", d: "Escrit a dalt, damunt del cavall. La ciutat on Porsche té la seu des del 1931." },
                    { t: "Les banyes", d: "Banyes de cérvol estilitzades, de l'escut de l'antic estat de Württemberg." },
                    { t: "Vermell i negre", d: "Els colors de l'escut de Württemberg, a banda i banda del cavall." },
                  ].map((x) => (
                    <div key={x.t} className="hp-reveal p-6" style={{ background: "var(--hp-bg)" }}>
                      <div className="font-semibold">{x.t}</div>
                      <p className="text-sm hp-muted mt-2 leading-relaxed">{x.d}</p>
                    </div>
                  ))}
                </div>
                <p className="hp-p mt-8 hp-reveal">
                  S'ha retocat el 1954, 1963, 1973, 1994, 2008 i 2023, però sempre amb canvis petits: menys ombres, colors
                  més nets, el cavall més definit.
                </p>
              </div>
            </div>
            <Foto
              src="escut-evolucio"
              alt="Evolució de l'escut de Porsche"
              cap="L'evolució de l'escut, del 1954 al 2008"
              className="mt-14"
              ratio="16/9"
            />
          </Wrap>
        </section>

        {/* 03 PROPIETAT */}
        <section className="hp-soft py-20 sm:py-32">
          <Wrap>
            <Capitol n="03" titol="De qui és Porsche" id="propietat" />
            <div className="grid md:grid-cols-12 gap-10">
              <div className="md:col-span-6">
                <h2 className="hp-h2 hp-reveal">Alemanya, Stuttgart i una família.</h2>
                <p className="hp-lead mt-6 hp-reveal">
                  Porsche és una marca alemanya amb seu a <strong style={{ color: "var(--hp-fg)" }}>Stuttgart-Zuffenhausen</strong>.
                  Forma part del <strong style={{ color: "var(--hp-fg)" }}>grup Volkswagen</strong>, però la història és
                  circular: la família Porsche controla Volkswagen.
                </p>
                <p className="hp-p mt-6 hp-reveal">
                  Entre el 2005 i el 2009, Porsche va intentar comprar Volkswagen, molt més gran. La crisi financera va
                  girar la jugada i va acabar sent Volkswagen qui absorbia Porsche (2012). Però els hereus de Ferdinand
                  Porsche, les famílies <strong>Porsche</strong> i <strong>Piëch</strong> (de la seva filla Louise), tenen
                  la majoria de vots de Volkswagen a través de la societat Porsche SE. El setembre del 2022, Porsche AG
                  va tornar a cotitzar a la borsa de Frankfurt.
                </p>
                <p className="hp-p mt-6 hp-reveal">
                  Es fabrica a <strong>Zuffenhausen</strong> (911, 718 i Taycan), <strong>Leipzig</strong> (Macan i
                  Panamera) i Bratislava (Cayenne), i ven prop de 300.000 cotxes l'any.
                </p>
                <p className="hp-p mt-6 hp-reveal">
                  Al grup també hi ha Audi, Lamborghini, Bentley, Ducati, SEAT, CUPRA i Škoda.{" "}
                  <Link to="/grups" className="underline underline-offset-4" style={{ color: "var(--hp-fg)" }}>
                    Mira tots els grups →
                  </Link>
                </p>
              </div>

              {/* Diagrama de propietat */}
              <div className="md:col-span-6 hp-reveal">
                <div className="flex flex-col items-stretch gap-0">
                  {[
                    { t: "Famílies Porsche i Piëch", s: "Néts i besnéts de Ferdinand Porsche" },
                    { t: "Porsche SE", s: "Societat de cartera. Té la majoria dels vots de Volkswagen" },
                    { t: "Volkswagen AG", s: "El grup. Té la gran majoria de Porsche AG" },
                    { t: "Porsche AG", s: "Els cotxes. Cotitza a la borsa des del 2022", red: true },
                  ].map((x, k, arr) => (
                    <div key={x.t} className="flex flex-col items-center">
                      <div
                        className="w-full p-5 sm:p-6 border"
                        style={{
                          background: x.red ? "var(--hp-fg)" : "var(--hp-bg)",
                          color: x.red ? "var(--hp-bg)" : "var(--hp-fg)",
                          borderColor: x.red ? "var(--hp-fg)" : "var(--hp-line)",
                        }}
                      >
                        <div className="hp-wide font-semibold text-lg">{x.t}</div>
                        <div className="text-sm opacity-70 mt-1">{x.s}</div>
                      </div>
                      {k < arr.length - 1 && (
                        <div className="flex flex-col items-center py-1">
                          <span className="block w-px h-6" style={{ background: "var(--hp-muted)" }} />
                          <span className="text-xs hp-muted">▼</span>
                        </div>
                      )}
                    </div>
                  ))}
                  <p className="hp-cap mt-4">
                    A més, Porsche SE també té directament el 25% (més una acció) de les accions amb vot de Porsche AG.
                  </p>
                </div>

                {/* Arbre familiar */}
                <div className="mt-10 pt-8 border-t hp-line">
                  <div className="hp-kicker mb-4">Tres generacions clau</div>
                  <ul className="space-y-4 text-sm">
                    <li>
                      <span className="font-semibold">Ferdinand Porsche</span>
                      <span className="hp-muted"> (1875–1951) · el fundador, l'enginyer del Volkswagen</span>
                    </li>
                    <li className="pl-5 border-l-2" style={{ borderColor: "var(--hp-red)" }}>
                      <span className="font-semibold">Ferry Porsche</span>
                      <span className="hp-muted"> (1909–1998) · el fill, el pare del 356 i del 911</span>
                    </li>
                    <li className="pl-5 border-l-2 hp-line">
                      <span className="font-semibold">Louise Piëch</span>
                      <span className="hp-muted"> (1904–1999) · la filla, al capdavant de Porsche a Àustria</span>
                    </li>
                    <li className="pl-10 border-l-2" style={{ borderColor: "var(--hp-red)" }}>
                      <span className="font-semibold">F. A. «Butzi» Porsche</span>
                      <span className="hp-muted"> · fill de Ferry, dissenyador del 911 i fundador de Porsche Design</span>
                    </li>
                    <li className="pl-10 border-l-2 hp-line">
                      <span className="font-semibold">Ferdinand Piëch</span>
                      <span className="hp-muted"> · fill de Louise, enginyer del 917 i després cap de Volkswagen</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </Wrap>
        </section>

        {/* 04 EL 911 */}
        <section className="py-20 sm:py-32">
          <Wrap>
            <Capitol n="04" titol="El 911" id="911" />
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-6">
                <NouCentOnze />
              </div>
              <div className="md:col-span-6 hp-reveal">
                <h2 className="hp-h3">Per què es diu 911 i no 901?</h2>
                <p className="hp-p mt-4">
                  El setembre del 1963 Porsche presenta al Saló de Frankfurt el successor del 356 amb el nom de{" "}
                  <strong>901</strong>, un número que encaixava amb la numeració de recanvis de Volkswagen.
                </p>
                <p className="hp-p mt-4">
                  Quan el cotxe havia de sortir a la venda, el 1964, <strong>Peugeot</strong> va protestar: des del 1929
                  feia servir números de tres xifres amb un zero al mig i en tenia els drets a França. Porsche no podia
                  vendre-hi un «901».
                </p>
                <p className="hp-p mt-4">
                  La solució va ser canviar el 0 per un 1. Com que la tipografia del «1» ja estava dibuixada per als
                  catàlegs i l'anagrama del darrere, només van haver de duplicar-la. Ja s'havien fet unes quantes unitats
                  com a 901, avui molt buscades.
                </p>
                <p className="hp-p mt-4">
                  Els cotxes de curses, que no es venien al públic, sí que van seguir portant el zero: 904, 906, 907,
                  908, 909...
                </p>
              </div>
            </div>

            <div className="mt-16 grid sm:grid-cols-2 gap-6">
              <Foto src="901" alt="Porsche 911 número 57, encara amb el nom 901" cap="911 (901 Nº 57), del museu Porsche" ratio="3/2" />
              <Foto src="901-perfil" alt="Perfil del 901 número 57" cap="La silueta que no ha canviat mai" ratio="3/2" />
            </div>

            <div className="mt-24 sm:mt-32">
              <div className="grid md:grid-cols-12 gap-6 mb-10">
                <h2 className="hp-h2 md:col-span-7 hp-reveal">Vuit generacions. Una sola silueta.</h2>
                <p className="hp-lead md:col-span-5 hp-reveal">
                  Cada generació té un codi intern. Els fans no diuen «el 911 del 1995»: diuen «un 993».
                </p>
              </div>
              <SelectorGeneracions />
            </div>
          </Wrap>
        </section>

        {/* 05 NOMS */}
        <section className="hp-darkband py-20 sm:py-32">
          <Wrap>
            <Capitol n="05" titol="Com funcionen els noms" id="noms" />
            <div className="grid md:grid-cols-12 gap-8">
              <h2 className="hp-h2 md:col-span-7 hp-reveal">Un número o un nom, i després els sufixos.</h2>
              <p className="md:col-span-5 hp-p hp-reveal">
                Des del 1931 cada projecte de Porsche té un <strong>número de tipus</strong>: el 60 era el Volkswagen i
                el 356, el primer Porsche. Avui conviuen els números (911, 718) i els noms (Cayenne, Taycan).
              </p>
            </div>

            {/* Un nom desglossat */}
            <div className="mt-14 grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-6">
                <Foto src="911-gts" alt="Porsche 911 Carrera GTS" cap="911 Carrera GTS, 2024" ratio="16/10" />
              </div>
              <div className="md:col-span-6 hp-reveal">
                <div className="hp-wide font-semibold leading-none" style={{ fontSize: "clamp(1.8rem, 4vw, 3rem)" }}>
                  911 Carrera 4 GTS
                </div>
                <div className="mt-8 border-t hp-line">
                  {anatomia.map((x) => (
                    <div key={x.t} className="grid grid-cols-[6.5rem_1fr] sm:grid-cols-[8rem_1fr] gap-4 py-4 border-b hp-line items-baseline">
                      <div className="hp-wide font-semibold text-xl sm:text-2xl hp-red">{x.t}</div>
                      <div>
                        <div className="hp-kicker">{x.l}</div>
                        <p className="text-sm hp-muted mt-1 leading-snug">{x.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* D'on surten els noms */}
            <h3 className="hp-h3 hp-reveal mt-24">D'on surten els noms</h3>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-10">
              {nomsFamilies.map((n) => (
                <article key={n.n} className="hp-reveal">
                  <div className="hp-zoom" style={{ aspectRatio: "16/10" }}>
                    <img src={IMG(n.img)} alt={`Porsche ${n.n}`} className="hp-img" loading="lazy" />
                  </div>
                  <div className="hp-wide font-semibold text-xl sm:text-2xl mt-4">{n.n}</div>
                  <div className="text-sm hp-red font-medium mt-1">{n.o}</div>
                  <p className="text-sm hp-muted mt-2 leading-relaxed">{n.d}</p>
                </article>
              ))}
            </div>

            {/* Sufixos */}
            <h3 className="hp-h3 hp-reveal mt-24">Els sufixos</h3>
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-x-6 hp-reveal">
              {sufixos.map((s) => (
                <div key={s.s} className="py-4 border-t hp-line">
                  <div className="hp-wide font-semibold text-xl">{s.s}</div>
                  <p className="text-sm hp-muted mt-1">{s.d}</p>
                </div>
              ))}
            </div>
          </Wrap>
        </section>

        {/* 06 PEUGEOT */}
        <section className="py-20 sm:py-32">
          <Wrap>
            <Capitol n="06" titol="El cas Peugeot" id="peugeot" />
            <div className="grid md:grid-cols-12 gap-8">
              <h2 className="hp-h2 md:col-span-7 hp-reveal">Per què Peugeot era l'amo del zero.</h2>
              <p className="hp-lead md:col-span-5 hp-reveal">
                El 1929 Peugeot va treure el 201 i va començar un sistema de noms que encara fa servir, gairebé 100 anys
                després. El va registrar a França, i per això va poder frenar el 901.
              </p>
            </div>

            {/* Diagrama 2 0 8 */}
            <div className="mt-16 hp-reveal grid grid-cols-3 max-w-3xl mx-auto text-center">
              {[
                { d: "2", t: "Mida", s: "1 molt petit … 6 luxe" },
                { d: "0", t: "Separador", s: "El zero central, la marca de la casa" },
                { d: "8", t: "Generació", s: "Puja a cada model nou" },
              ].map((x, k) => (
                <div key={k} className={`px-2 ${k === 1 ? "border-x hp-line" : ""}`}>
                  <div
                    className="hp-num font-semibold"
                    style={{ fontSize: "clamp(5rem, 18vw, 11rem)", color: k === 1 ? "var(--hp-red)" : undefined }}
                  >
                    {x.d}
                  </div>
                  <div className="font-semibold mt-2">{x.t}</div>
                  <div className="text-xs sm:text-sm hp-muted mt-1">{x.s}</div>
                </div>
              ))}
            </div>

            <div className="mt-16 hp-reveal border-t hp-line">
              {peugeotSeries.map((p) => (
                <div key={p.x} className="grid grid-cols-12 gap-3 py-4 border-b hp-line items-baseline">
                  <div className="col-span-2 sm:col-span-1 hp-num text-3xl font-semibold">{p.x}</div>
                  <div className="col-span-10 sm:col-span-2 text-sm font-medium">{p.d}</div>
                  <div className="col-span-12 sm:col-span-9 text-sm hp-muted font-mono">{p.models}</div>
                </div>
              ))}
            </div>

            <div className="mt-16 grid md:grid-cols-3 gap-8">
              {[
                {
                  t: "Dos zeros: SUV i monovolums",
                  d: "El 2004 arriba el 1007 i, després, el 3008, el 5008 i el 2008. El doble zero diu que és un SUV o un monovolum; la primera xifra, la mida.",
                },
                {
                  t: "El 8 es queda",
                  d: "Peugeot va dir que el 208, el 308 i el 508 tindrien el 8 per sempre: quan surt un model nou, ja no puja de número. Per això hi ha un 208 de 2012 i un altre de 2019.",
                },
                {
                  t: "La revenja del 908",
                  d: "Porsche va córrer amb el 908 als anys seixanta. El 2007 Peugeot va tornar a Le Mans amb un cotxe que es deia... 908. I hi va guanyar el 2009.",
                },
              ].map((x) => (
                <div key={x.t} className="hp-reveal hp-soft p-6 sm:p-8">
                  <div className="font-semibold text-lg">{x.t}</div>
                  <p className="text-sm hp-muted mt-3 leading-relaxed">{x.d}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 hp-reveal hp-p">
              Vols veure com funcionen els noms d'altres marques (BMW, Mercedes, Audi...)?{" "}
              <Link to="/guia-models" className="underline underline-offset-4" style={{ color: "var(--hp-fg)" }}>
                Guia de models →
              </Link>
            </p>
          </Wrap>
        </section>

        {/* 07 MODELS */}
        <section className="py-20 sm:py-32 border-t hp-line">
          <Wrap>
            <Capitol n="07" titol="Models que van fer història" id="models" />
            <h2 className="hp-h2 hp-reveal max-w-4xl">De les curses als SUV, i dels SUV a l'elèctric.</h2>
            <div className="mt-16 sm:mt-24 space-y-20 sm:space-y-32">
              {fites.map((f, i) => (
                <FitaBloc key={f.nom} f={f} i={i} />
              ))}
            </div>
          </Wrap>
        </section>

        {/* Gulf: imatge a tota amplada */}
        <section className="relative hp-reveal">
          <div style={{ aspectRatio: "16/7" }} className="overflow-hidden bg-black">
            <img src={IMG("917-gulf")} alt="Porsche 917 amb els colors de Gulf al túnel de vent" className="hp-img" loading="lazy" />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
            <Wrap className="pb-6 sm:pb-12 w-full text-white">
              <div className="hp-mark text-xl sm:text-4xl">917</div>
              <p className="text-white/75 text-sm mt-2 max-w-md">
                Un 917 amb els colors de Gulf. El 1971, el 917 Martini va fer a Le Mans un rècord de distància que va durar 39 anys. © Porsche AG
              </p>
            </Wrap>
          </div>
        </section>

        {/* Transaxle */}
        <section className="hp-darkband py-20 sm:py-32">
          <Wrap>
            <div className="grid md:grid-cols-12 gap-8 mb-12">
              <div className="md:col-span-6 hp-reveal">
                <div className="hp-kicker">1976–1995</div>
                <h2 className="hp-h2 mt-4">L'era Transaxle</h2>
              </div>
              <p className="md:col-span-6 hp-p hp-reveal">
                Porsche va pensar que el 911 tenia els dies comptats i va crear una família amb el{" "}
                <strong>motor davant i el canvi darrere</strong>, a l'eix posterior, per repartir bé el pes. El 924 venia
                d'un projecte de Volkswagen; el 928, amb un V8, és l'únic esportiu que ha guanyat el Cotxe de l'Any a
                Europa (1978). En total, gairebé 400.000 cotxes. El 911, és clar, va sobreviure.
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { n: "924", a: "1976", img: "924" },
                { n: "928", a: "1977", img: "928" },
                { n: "944", a: "1981", img: "944" },
                { n: "968", a: "1991", img: "968" },
              ].map((t) => (
                <figure key={t.n} className="hp-reveal">
                  <div className="hp-zoom" style={{ aspectRatio: "4/5" }}>
                    <img src={IMG(t.img)} alt={`Porsche ${t.n}, il·lustració`} className="hp-img" loading="lazy" />
                  </div>
                  <figcaption className="mt-3 flex items-baseline justify-between">
                    <span className="hp-wide font-semibold text-xl">{t.n}</span>
                    <span className="text-sm hp-muted">{t.a}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
            <p className="hp-cap">Il·lustracions dels 50 anys de Transaxle, 2026 · © Porsche AG</p>
          </Wrap>
        </section>

        <section className="py-20 sm:py-32">
          <Wrap>
            <div className="space-y-20 sm:space-y-32">
              {fites2.map((f, i) => (
                <FitaBloc key={f.nom} f={f} i={i + 1} />
              ))}
            </div>
            <div className="mt-20 grid sm:grid-cols-3 gap-6">
              <Foto src="959-dakar" alt="Porsche 959 París-Dakar" cap="959 París-Dakar" ratio="4/3" />
              <Foto src="trio-2002" alt="Cayenne, 911 i Boxster el 2002" cap="Cayenne, 911 i Boxster, 2002" ratio="4/3" />
              <Foto src="mission-e" alt="Porsche Mission E" cap="Mission E, el concept del Taycan, 2015" ratio="4/3" />
            </div>
          </Wrap>
        </section>

        {/* 08 AVUI */}
        <section className="hp-soft py-20 sm:py-32">
          <Wrap>
            <Capitol n="08" titol="Avui" id="avui" />
            <div className="grid md:grid-cols-12 gap-8 mb-12">
              <h2 className="hp-h2 md:col-span-7 hp-reveal">Sis famílies.</h2>
              <p className="hp-lead md:col-span-5 hp-reveal">
                La gamma del 2026, amb combustió, híbrids i elèctrics alhora. El 718 de gasolina s'acomiada i el seu relleu
                serà elèctric.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {gamma.map((g) => (
                <article key={g.n} className="hp-reveal">
                  <div className="hp-zoom" style={{ aspectRatio: "16/10", background: "var(--hp-bg)" }}>
                    <img src={IMG(g.img)} alt={`Porsche ${g.n}`} className="hp-img" loading="lazy" />
                  </div>
                  <div className="mt-4 flex items-baseline justify-between gap-4">
                    <h3 className="hp-wide font-semibold text-2xl">{g.n}</h3>
                    <span className="text-xs hp-muted text-right">{g.m}</span>
                  </div>
                  <p className="text-sm hp-muted mt-1">{g.t}</p>
                </article>
              ))}
            </div>
            <div className="mt-12 hp-reveal">
              <Link to="/porsche" className="hp-btn">
                Tots els models amb preus i dades →
              </Link>
            </div>
          </Wrap>
        </section>

        {/* Futur */}
        <section className="hp-darkband py-20 sm:py-32">
          <Wrap>
            <div className="hp-kicker hp-reveal">I ara què?</div>
            <h2 className="hp-h2 mt-4 hp-reveal max-w-4xl">Els concepts diuen cap a on va.</h2>
            <div className="mt-14 grid md:grid-cols-3 gap-6">
              {[
                { img: "mission-x", n: "Mission X", a: "2023", d: "Hipercotxe elèctric, presentat pels 75 anys. Portes que s'obren cap amunt com als cotxes de Le Mans." },
                { img: "vision-357", n: "Vision 357", a: "2023", d: "Homenatge al 356, el primer Porsche, amb la mecànica d'un 718." },
                { img: "", n: "Mission S", a: "2026", d: "Superesportiu de motor central de combustió, successor espiritual del 918. Presentat el 15 d'octubre a Nova York." },
              ].map((c) => (
                <article key={c.n} className="hp-reveal">
                  <div className="hp-zoom" style={{ aspectRatio: "4/3", background: "#000" }}>
                    <img
                      src={c.img ? IMG(c.img) : "/novetats/porsche-mission-s.webp"}
                      alt={`Porsche ${c.n}`}
                      className="hp-img"
                      loading="lazy"
                    />
                  </div>
                  <div className="mt-4 flex items-baseline justify-between">
                    <h3 className="hp-wide font-semibold text-xl">{c.n}</h3>
                    <span className="text-sm hp-muted">{c.a}</span>
                  </div>
                  <p className="text-sm hp-muted mt-2 leading-relaxed">{c.d}</p>
                </article>
              ))}
            </div>
            <p className="mt-10 hp-reveal hp-p">
              <Link to="/novetats" className="underline underline-offset-4" style={{ color: "var(--hp-fg)" }}>
                Més sobre el Mission S a Novetats →
              </Link>
            </p>
          </Wrap>
        </section>

        {/* Fonts */}
        <section className="py-16">
          <Wrap>
            <div className="hp-mark text-center text-lg sm:text-2xl hp-reveal pl-[0.32em]">Porsche</div>
            <div className="mt-10 pt-8 border-t hp-line grid md:grid-cols-12 gap-6 text-xs hp-muted">
              <p className="md:col-span-4 leading-relaxed">
                Totes les fotos són material de premsa oficial de Porsche AG (newsroom.porsche.com). Àudio de la pronunciació: Keosintal, Wikimedia Commons, CC BY-SA 3.0. La tipografia imita
                la de Porsche amb Archivo, perquè la Porsche Next és d'ús exclusiu de la marca. Actualitzat el 16
                d'octubre de 2026.
              </p>
              <div className="md:col-span-8">
                <div className="font-semibold mb-2" style={{ color: "var(--hp-fg)" }}>
                  Fonts
                </div>
                <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                  {fonts.map((f) => (
                    <li key={f.url}>
                      <a href={f.url} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:opacity-80">
                        {f.nom}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </Wrap>
        </section>
      </div>
      <ScrollToTop />
    </>
  );
};

export default HistoriaPorsche;
